<template>
  <svg class="clock-face"
       viewBox="0 0 240 240"
       width="240"
       height="240">
    <circle cx="120"
            cy="120"
            r="118"
            class="clock-face__bg" />

    <!-- center dot -->
    <circle cx="120"
            cy="120"
            r="3"
            class="clock-face__center" />

    <!-- hand to the selected value -->
    <line v-if="hand"
          :x1="120"
          :y1="120"
          :x2="hand.x"
          :y2="hand.y"
          class="clock-face__hand" />
    <circle v-if="hand"
            :cx="hand.x"
            :cy="hand.y"
            r="16"
            class="clock-face__hand-dot" />

    <template v-if="mode === 'hour'">
      <ClockNumber v-for="h in outerHours"
                   :key="`o-${h.value}`"
                   :value="h.value"
                   :x="h.x"
                   :y="h.y"
                   :display="h.display"
                   :selected="selectedHour === h.value"
                   @select="$emit('select', $event)" />
      <ClockNumber v-for="h in innerHours"
                   :key="`i-${h.value}`"
                   :value="h.value"
                   :x="h.x"
                   :y="h.y"
                   :display="h.display"
                   :selected="selectedHour === h.value"
                   @select="$emit('select', $event)" />
    </template>

    <template v-else>
      <ClockNumber v-for="m in minutes"
                   :key="`m-${m.value}`"
                   :value="m.value"
                   :x="m.x"
                   :y="m.y"
                   :display="m.display"
                   :selected="selectedMinute === m.value"
                   @select="$emit('select', $event)" />
    </template>
  </svg>
</template>

<script>
import ClockNumber from './ClockNumber.vue'

const CENTER = 120
const OUTER_R = 96
const INNER_R = 66

function pos (radius, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad)
  }
}

export default {
  name: 'ClockFace',
  components: { ClockNumber },
  props: {
    mode: {
      type: String,
      default: 'hour',
      validator: (v) => v === 'hour' || v === 'minute'
    },
    selectedHour: {
      type: Number,
      default: 0
    },
    selectedMinute: {
      type: Number,
      default: 0
    }
  },
  computed: {
    outerHours () {
      const out = []
      for (let h = 1; h <= 12; h++) {
        const angle = (h % 12) * 30
        const p = pos(OUTER_R, angle)
        out.push({ value: h, x: p.x, y: p.y, display: String(h) })
      }
      return out
    },
    innerHours () {
      const out = []
      const order = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
      for (let i = 0; i < 12; i++) {
        const angle = i * 30
        const p = pos(INNER_R, angle)
        const v = order[i]
        out.push({ value: v, x: p.x, y: p.y, display: v === 0 ? '00' : String(v) })
      }
      return out
    },
    minutes () {
      const out = []
      for (let m = 0; m < 60; m += 5) {
        const angle = (m / 5) * 30
        const p = pos(OUTER_R, angle)
        out.push({ value: m, x: p.x, y: p.y, display: String(m).padStart(2, '0') })
      }
      return out
    },
    hand () {
      if (this.mode === 'hour') {
        const h = this.selectedHour
        if (h >= 1 && h <= 12) return pos(OUTER_R, (h % 12) * 30)
        if (h === 0) return pos(INNER_R, 0)
        if (h >= 13 && h <= 23) return pos(INNER_R, (h - 12) * 30)
        return null
      }
      const m = this.selectedMinute
      if (m % 5 !== 0) return null
      return pos(OUTER_R, (m / 5) * 30)
    }
  }
}
</script>

<style lang="less">
@import "../../styles/var";

.clock-face {
  display: block;

  &__bg {
    fill: @light-gray;
  }

  &__center {
    fill: @blue;
  }

  &__hand {
    stroke: @blue;
    stroke-width: 2;
  }

  &__hand-dot {
    fill: @blue;
    opacity: 0.25;
    pointer-events: none;
  }
}
</style>
